import { useState } from 'react';
import { RefreshCw, User, Send, ExternalLink, Link2, Unlink2, X } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { redtailApi, queryKeys, Redtail } from '@client/api/integrations';
import { useDebounce } from '@client/hooks/useDebounce';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { useInviteClientTrigger, Tag } from '@/components/invite-client/InviteClientContext';
import { LinkClientModal } from './LinkClientModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

function getPrimaryEmail(emails: Redtail.Contact['emails']): string | undefined {
  if (!emails || emails.length === 0) return undefined;
  const primary = emails.find(e => e.isPrimary);
  return primary?.address ?? emails[0]?.address;
}

interface ContactRowProps {
  contact: Redtail.Contact;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  onViewClient: (clientId: string) => void;
  onLink: (contact: Redtail.Contact) => void;
  onUnlink: (contact: Redtail.Contact) => void;
  isUnlinking: boolean;
}

function ContactRow({
  contact,
  isSelected,
  onToggleSelect,
  onViewClient,
  onLink,
  onUnlink,
  isUnlinking,
}: ContactRowProps) {
  const hasEmail = contact.emails && contact.emails.length > 0;
  const isBusiness = contact.type?.toLowerCase() === 'business';
  const isDisabled = !hasEmail || isBusiness;

  return (
    <TableRow
      className={`group ${isDisabled ? 'opacity-60' : 'hover:bg-gray-50'}`}
      data-state={isSelected ? 'selected' : undefined}
    >
      <TableCell className="w-14 pl-4">
        <div className="flex h-8 w-8 items-center justify-center">
          {isDisabled ? (
            <User className="h-5 w-5 text-gray-300" />
          ) : isSelected ? (
            <Checkbox
              checked={true}
              onCheckedChange={() => onToggleSelect(contact.id)}
              aria-label={`Select ${contact.firstName} ${contact.lastName}`}
            />
          ) : (
            <>
              <User className="h-5 w-5 text-gray-400 group-hover:hidden" />
              <Checkbox
                checked={false}
                onCheckedChange={() => onToggleSelect(contact.id)}
                className="hidden group-hover:flex"
                aria-label={`Select ${contact.firstName} ${contact.lastName}`}
              />
            </>
          )}
        </div>
      </TableCell>
      <TableCell className="font-medium text-gray-900">
        {contact.firstName ?? contact.companyName} {contact.lastName}
      </TableCell>
      <TableCell className="text-gray-700">
        {getPrimaryEmail(contact.emails) || <span className="text-gray-400">No email</span>}
      </TableCell>
      <TableCell className="text-gray-700">{contact.type}</TableCell>
      <TableCell className="text-gray-700">{contact.status}</TableCell>
      <TableCell className="text-gray-700">
        {contact.waterlilyClientId ? (
          <button
            onClick={() => onViewClient(contact.waterlilyClientId!)}
            className="text-primary hover:text-primary-hover flex items-center gap-1 hover:underline"
          >
            View
            <ExternalLink className="h-3 w-3" />
          </button>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </TableCell>
      <TableCell className="text-gray-700">
        {!isDisabled &&
          (contact.waterlilyClientId ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onUnlink(contact)}
              disabled={isUnlinking}
              className="border-red-200 text-red-600 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
            >
              <Unlink2 className="h-4 w-4" />
              Unlink
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onLink(contact)}
            >
              <Link2 className="h-4 w-4" />
              Link
            </Button>
          ))}
      </TableCell>
    </TableRow>
  );
}

export function Contacts() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [contactToLink, setContactToLink] = useState<Redtail.Contact | null>(null);
  const [contactToUnlink, setContactToUnlink] = useState<Redtail.Contact | null>(null);
  const { setInviteClientParams } = useInviteClientTrigger();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const pageSize = 10;

  const handleViewClient = (clientId: string) => {
    navigate(`/clients/${clientId}/onboarding/results-are-in`);
  };

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.redtail.list('contacts', { page, pageSize, query: debouncedSearch }),
    queryFn: () =>
      redtailApi.contacts.list({
        page: String(page),
        pageSize: String(pageSize),
        query: debouncedSearch || undefined,
      }),
  });

  const unlinkMutation = useMutation({
    mutationFn: (contactId: string) => redtailApi.contacts.unlink(contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.redtail.lists('contacts') });
    },
  });

  const contacts = data?.contacts ?? [];
  const totalPages = data?.meta?.totalPages ?? 1;

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectableContacts = contacts.filter(c => {
    const hasEmail = c.emails && c.emails.length > 0;
    const isBusiness = c.type?.toLowerCase() === 'business';
    return hasEmail && !isBusiness;
  });

  const toggleSelectAll = () => {
    if (selectedIds.size === selectableContacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableContacts.map(c => c.id)));
    }
  };

  const allSelected =
    selectableContacts.length > 0 && selectedIds.size === selectableContacts.length;

  const selectedContacts = contacts.filter(c => selectedIds.has(c.id));
  const unlinkedSelectedContacts = selectedContacts.filter(c => !c.waterlilyClientId);

  const selectedEmailTags: Tag[] = unlinkedSelectedContacts
    .filter(c => c.emails && c.emails.length > 0)
    .map(c => ({
      value: getPrimaryEmail(c.emails)!,
      isValid: true,
      error: '',
      contactId: c.id,
    }));

  const canInvite = unlinkedSelectedContacts.length > 0;

  const handleImport = () => {
    const singleContactId =
      unlinkedSelectedContacts.length === 1 ? unlinkedSelectedContacts[0].id : undefined;
    setInviteClientParams('email', selectedEmailTags, singleContactId);
    setSelectedIds(new Set());
  };

  const confirmUnlink = () => {
    if (contactToUnlink) {
      unlinkMutation.mutate(contactToUnlink.id);
      setSelectedIds(new Set());
      setContactToUnlink(null);
    }
  };

  let placeholder;
  if (isFetching) {
    placeholder = (
      <TableRow>
        <TableCell
          colSpan={7}
          className="h-48"
        >
          <div className="flex items-center justify-center">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        </TableCell>
      </TableRow>
    );
  } else if (error) {
    placeholder = (
      <TableRow>
        <TableCell
          colSpan={7}
          className="h-48"
        >
          <div className="flex items-center justify-center text-red-500">
            {error.message || 'Failed to load contacts'}
          </div>
        </TableCell>
      </TableRow>
    );
  } else if (contacts.length === 0) {
    placeholder = (
      <TableRow>
        <TableCell
          colSpan={7}
          className="h-48"
        >
          <div className="flex items-center justify-center text-gray-500">
            {search ? 'No contacts match your search' : 'No contacts found'}
          </div>
        </TableCell>
      </TableRow>
    );
  } else {
    placeholder = contacts.map(contact => (
      <ContactRow
        key={contact.id}
        contact={contact}
        isSelected={selectedIds.has(contact.id)}
        onToggleSelect={toggleSelect}
        onViewClient={handleViewClient}
        onLink={setContactToLink}
        onUnlink={setContactToUnlink}
        isUnlinking={unlinkMutation.isPending}
      />
    ));
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          {selectedIds.size > 0 ? (
            <>
              <div className="p-1 text-sm font-medium text-gray-900">
                {selectedIds.size} {selectedIds.size === 1 ? 'Contact' : 'Contacts'} selected
              </div>
              <div className="flex items-center gap-2">
                {canInvite && (
                  <Button onClick={handleImport}>
                    <Send className="h-4 w-4" />
                    Invite
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="relative w-80">
                <Input
                  placeholder="Search contacts..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pr-8"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <Button
                variant="outline"
                onClick={() => refetch()}
                disabled={isFetching}
              >
                <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </>
          )}
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-14 pl-4">
                  <div className="flex h-8 w-8 items-center justify-center">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all"
                      disabled={isFetching || !!error}
                    />
                  </div>
                </TableHead>
                <TableHead className="text-gray-900">Name</TableHead>
                <TableHead className="text-gray-900">Email</TableHead>
                <TableHead className="text-gray-900">Type</TableHead>
                <TableHead className="text-gray-900">Status</TableHead>
                <TableHead className="text-gray-900">Waterlily</TableHead>
                <TableHead className="text-gray-900">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>{placeholder}</TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => p - 1)}
            disabled={page <= 1 || isFetching}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-500">Page {page}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => p + 1)}
            disabled={page >= totalPages || isFetching}
          >
            Next
          </Button>
        </div>
      </div>

      <LinkClientModal
        open={!!contactToLink}
        onClose={() => {
          setContactToLink(null);
          setSelectedIds(new Set());
        }}
        contact={contactToLink}
      />

      <AlertDialog
        open={!!contactToUnlink}
        onOpenChange={open => !open && setContactToUnlink(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unlink{' '}
              <span className="font-medium text-gray-900">
                {contactToUnlink?.firstName ?? contactToUnlink?.companyName}{' '}
                {contactToUnlink?.lastName}
              </span>{' '}
              from their Waterlily client? This would also remove waterlily's user defined fields
              from Redtails
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmUnlink}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {unlinkMutation.isPending ? 'Unlinking...' : 'Unlink'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
